import { useOutletContext } from "react-router-dom";
import useSWR from "swr";
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import KeyValItem from "../../../components/KeyValItem";
import { genderFmt, phoneFmt } from "../../../formatters";
import IntakeRequirementsAlert from "./IntakeRequirementsAlert";

export default function IntakeDemos({ intakeId }) {
  const { api } = useOutletContext();
  const { isLoading, error, data } = useSWR(`/intake/${intakeId}`, () =>
    api.getIntake(intakeId),
  );

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!data) return null;

  const serviceChannel = data.sc_override ?? data.computed_service_channel;

  return (
    <>
      <Typography variant="h3" sx={{ textAlign: "center" }}>
        {data.name}
      </Typography>
      <IntakeRequirementsAlert intakeId={intakeId} />
      <Card sx={{ borderRadius: 4 }}>
        <CardHeader title="Demographics" />
        <CardContent>
          <Stack spacing={1}>
            <KeyValItem label="Gender" val={genderFmt(data.gender)} />
            <KeyValItem label="DOB" val={data.dob} />
            <KeyValItem
              label="Address"
              val={
                <>
                  {data.address.split("\n").map((l, i) => (
                    <Typography variant="body2" key={i}>
                      {l}
                    </Typography>
                  ))}
                </>
              }
            />
            <KeyValItem label="Email" val={data.email} />
            <KeyValItem
              label="Phone"
              val={
                data.phone ? (
                  <Typography variant="body2">
                    <Link href={`tel:${data.phone}`}>
                      {phoneFmt(data.phone)}
                    </Link>
                  </Typography>
                ) : (
                  ""
                )
              }
            />
            <KeyValItem
              label="Status"
              val={
                <Chip
                  variant="outlined"
                  size="small"
                  label={data.status}
                  color={data.status === "incomplete" ? "error" : "success"}
                />
              }
            />
            <KeyValItem
              label="Billing Channel"
              val={
                <Chip
                  size="small"
                  variant="outlined"
                  label={data.billing_channel}
                  color={
                    data.billing_channel === "work-comp"
                      ? "primary"
                      : data.billing_channel === "cash-pay"
                        ? "success"
                        : undefined
                  }
                />
              }
            />
            <KeyValItem
              label="Service Channel"
              val={
                <Chip
                  size="small"
                  variant="outlined"
                  label={serviceChannel}
                  color={serviceChannel === "agency" ? "error" : "success"}
                />
              }
            />
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
